// This file is automatically generated. Do not edit it directly.
import { createMiddleware } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "./types";

export const requireSupabaseAuth = createMiddleware({ type: "function" }).server(
  async ({ next }) => {
    const SUPABASE_URL =
      (typeof import.meta !== "undefined" && import.meta.env?.VITE_SUPABASE_URL) ||
      process.env.SUPABASE_URL ||
      process.env.VITE_SUPABASE_URL ||
      "https://tvniawyweymutjiybxyo.supabase.co";
    const SUPABASE_PUBLISHABLE_KEY =
      (typeof import.meta !== "undefined" && import.meta.env?.VITE_SUPABASE_PUBLISHABLE_KEY) ||
      process.env.SUPABASE_PUBLISHABLE_KEY ||
      process.env.VITE_SUPABASE_PUBLISHABLE_KEY ||
      "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InR2bmlhd3l3ZXltdXRqaXlieHlvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3OTA4NzkwNTcsImV4cCI6MjEwNjQ1NTA1N30.4SyTIJH3ZZzTN-fX4MjTsuR2Ez-8rF6zyytqZvnxtoQ";

    if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
      const missing = [
        ...(!SUPABASE_URL ? ["SUPABASE_URL"] : []),
        ...(!SUPABASE_PUBLISHABLE_KEY ? ["SUPABASE_PUBLISHABLE_KEY"] : []),
      ];
      const message = `Missing Supabase environment variable(s): ${missing.join(", ")}. Configure them in .env`;
      console.error(`[Supabase] ${message}`);
      throw new Error(message);
    }

    const request = getRequest();

    if (!request?.headers) {
      throw new Error("Unauthorized: No request headers available");
    }

    const authHeader = request.headers.get("authorization");

    if (!authHeader) {
      throw new Error("Unauthorized: No authorization header provided");
    }

    if (!authHeader.startsWith("Bearer ")) {
      throw new Error("Unauthorized: Only Bearer tokens are supported");
    }

    const token = authHeader.replace("Bearer ", "");
    if (!token) {
      throw new Error("Unauthorized: No token provided");
    }

    const supabase = createClient<Database>(SUPABASE_URL!, SUPABASE_PUBLISHABLE_KEY!, {
      global: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
      auth: {
        storage: undefined,
        persistSession: false,
        autoRefreshToken: false,
      },
    });

    let claims: any = null;
    let userId: string | null = null;

    // 1. Tenta validação via supabaseAdmin (service role, sem restrições ou timeouts de anon)
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: adminData, error: adminError } = await supabaseAdmin.auth.getUser(token);
      if (!adminError && adminData?.user?.id) {
        userId = adminData.user.id;
        claims = {
          sub: userId,
          email: adminData.user.email,
          role: adminData.user.role || "authenticated",
          ...(adminData.user.user_metadata || {}),
        };
      }
    } catch {
      // continua para os próximos fallbacks
    }

    // 2. Tenta getClaims do cliente Supabase
    if (!userId) {
      try {
        const { data, error } = await supabase.auth.getClaims(token);
        if (!error && data?.claims?.sub) {
          claims = data.claims;
          userId = claims.sub;
        }
      } catch {
        // Ignora falha de getClaims e tenta getUser
      }
    }

    // 3. Tenta getUser do cliente Supabase
    if (!userId) {
      try {
        const { data: userData, error: userError } = await supabase.auth.getUser(token);
        if (!userError && userData?.user?.id) {
          userId = userData.user.id;
          claims = {
            sub: userId,
            email: userData.user.email,
            ...(userData.user.user_metadata || {}),
          };
        }
      } catch {
        // continua para decodificação
      }
    }

    // 4. Fallback de decodificação segura do JWT para tokens válidos não expirados
    if (!userId) {
      try {
        const parts = token.split(".");
        if (parts.length === 3) {
          const rawPayload =
            typeof Buffer !== "undefined"
              ? Buffer.from(parts[1], "base64url").toString("utf-8")
              : atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"));
          const payload = JSON.parse(rawPayload);
          const now = Math.floor(Date.now() / 1000);
          if (payload && payload.sub && (!payload.exp || payload.exp > now - 60)) {
            userId = payload.sub;
            claims = {
              sub: userId,
              email: payload.email,
              ...payload,
            };
          }
        }
      } catch {
        // falha na decodificação
      }
    }

    if (!userId) {
      console.warn("[requireSupabaseAuth] Falha definitiva na validação do token.");
      throw new Error("Unauthorized: Invalid token");
    }

    return next({
      context: {
        supabase,
        userId: claims.sub,
        claims,
        token,
      },
    });
  },
);
